import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { groupPublicUrl, groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import GroupLoadState, { GroupRefreshBanner, shouldRetryGroupLoad } from './GroupLoadState';
import AskTripsbyPanel from '../../components/groups/assistant/AskTripsbyPanel';
import BroadcastsTab from '../../components/groups/BroadcastsTab';
import ConciergeTab from '../../components/groups/ConciergeTab';
import DinnersTab from '../../components/groups/DinnersTab';
import HostExportTab from '../../components/groups/export/HostExportTab';
import GuestsTab from '../../components/groups/GuestsTab';
import ItineraryTab from '../../components/groups/ItineraryTab';
import OverviewTab from '../../components/groups/OverviewTab';
import PublishToggle from '../../components/groups/PublishToggle';
import ReservationsTab from '../../components/groups/ReservationsTab';
import RoomBlocksTab from '../../components/groups/RoomBlocksTab';
import SeatingTab from '../../components/groups/SeatingTab';
import type { GroupDetail as GroupDetailType, GroupInvitee, GroupInviteeInput, SendGroupInvitesRequest } from '../../types/groups';

// Re-import a single invitee through the import/upsert path (keyed by group +
// email, or group + source + source_external_id) to persist a segment change.
// segment '' = explicit clear; null would mean "preserve" and never fires here.
/**
 * A one-guest segment change, sent down the import endpoint.
 *
 * `inviteeId` is the whole point: a guest imported from Partiful has no email,
 * no phone and no export id, so without it the write has nothing to match on and
 * changing their segment would ADD a second copy of them.
 */
function inviteeToInput(invitee: GroupInvitee, segment: string | null): GroupInviteeInput {
  return {
    inviteeId: invitee.id,
    fullName: invitee.fullName,
    email: invitee.email || null,
    phone: invitee.phone || null,
    rsvpStatus: invitee.rsvpStatus,
    source: invitee.source,
    sourceExternalId: invitee.sourceExternalId || null,
    notes: invitee.notes,
    segment,
  };
}

/**
 * The group page shell: header, tabs, and the group query (a 404/403 is final and says so: GroupLoadState).
 *
 * Each tab body is its own component and owns the writes it fires; the only
 * cache control handed down is `refresh`, so the ['group', id] key is stated
 * once, here. Keeping the page this thin is deliberate — it was 944 lines with
 * five tab bodies inlined, and the next feature had nowhere to go but into it.
 */
export default function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const { data: group, isLoading, error, refetch } = useQuery<GroupDetailType>(['group', id], () => groupsApi.getGroup(id!), { enabled: !!id, retry: shouldRetryGroupLoad });

  const refresh = () => queryClient.invalidateQueries(['group', id]);
  const inviteeImportMutation = useMutation(
    ({ invitees, rosterFingerprint }: { invitees: GroupInviteeInput[]; rosterFingerprint?: string }) =>
      groupsApi.importInvitees(id!, invitees, rosterFingerprint),
    { onSuccess: refresh },
  );
  const sendInvitesMutation = useMutation((body: SendGroupInvitesRequest) => groupsApi.sendInvites(id!, body));

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  // Only a page that never loaded is replaced; a failed background refetch keeps `data`, so it gets a banner instead (below).
  if (!group) return <GroupLoadState error={error} onRetry={() => { void refetch(); }} />;

  /*
    Preview has to carry an invite token on an invite-only trip.

    Without one the guest page has nothing to resolve the viewer from, the API
    returns null, and TripsbyWeb renders "This invitation needs its original
    link" — so the host's own Preview button was guaranteed to land on the dead
    end, on the one access mode where previewing matters most. The invite send
    path already passes `invitee.inviteToken`; this one passed nothing.

    Any guest's token shows the real page. It is their view — their booking, their
    claims — which the host can already read in full two tabs away, so this
    discloses nothing new; it just makes the button honest about whose page it
    opens. A public trip needs no token and keeps the plain link.
  */
  const previewAs = group.accessMode === 'invite_only'
    ? (group.invitees ?? []).find((invitee) => invitee.inviteToken)
    : undefined;
  const previewUrl = groupPublicUrl(group.slug, previewAs?.inviteToken ?? undefined);

  return (
    <Box>
      {error ? <GroupRefreshBanner error={error} onRetry={() => { void refetch(); }} /> : null}
      {/* Hidden for print, along with the header and tabs below — see HostExportTab. */}
      <Button onClick={() => navigate('/groups')} sx={{ mb: 2, '@media print': { display: 'none' } }}>Back to Groups</Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, '@media print': { display: 'none' } }}>
        <Box>
          {/* variant h4 renders an <h4>, which left the page with no h1 for the
              tab content's own headings to sit under. */}
          <Typography variant="h4" component="h1" fontWeight={800}>{group.title}</Typography>
          <Typography color="text.secondary">/g/{group.slug} · {group.locationName || 'Location TBD'}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <PublishToggle group={group} onSaved={refresh} />
          <Chip label={group.accessMode === 'invite_only' ? 'Invite-only' : 'Public'} color={group.accessMode === 'invite_only' ? 'warning' : 'default'} />
          <Button
            variant="outlined"
            onClick={() => window.open(previewUrl, '_blank')}
            title={
              previewAs
                ? `Opens the guest page as ${previewAs.fullName || 'a guest'} would see it`
                : 'Opens the guest page'
            }
          >
            {previewAs ? 'Preview as a guest' : 'Preview'}
          </Button>
        </Stack>
      </Box>
      {/*
        Named, not positional. These were array indices until Invitees was folded
        into Guests, at which point removing one tab silently shifted every tab
        after it onto the wrong panel — a rename is now the only way to break the
        mapping, and it breaks loudly.
      */}
      {/*
        scrollable + allowScrollButtonsMobile, because nine tabs do not fit a
        phone. Without it MUI clips the row at the viewport edge with no
        affordance: Dinners, Seating and Broadcasts were simply unreachable on
        mobile — a host could not send a broadcast or seat a table from their
        phone, and nothing on screen said why.
      */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, '@media print': { display: 'none' } }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="itinerary" label="Itinerary" />
        <Tab value="rooms" label="Room Blocks" />
        <Tab value="guests" label="Guests" />
        <Tab value="concierge" label="Concierge" />
        <Tab value="requests" label="Requests" />
        <Tab value="dinners" label="Dinners" />
        <Tab value="seating" label="Seating" />
        <Tab value="broadcasts" label="Broadcasts" />
        <Tab value="export" label="Export" />
      </Tabs>
      {tab === 'overview' && <OverviewTab group={group} onSaved={refresh} />}
      {tab === 'itinerary' && <ItineraryTab group={group} onChanged={refresh} />}
      {tab === 'rooms' && <RoomBlocksTab group={group} onChanged={refresh} />}
      {tab === 'guests' && (
        <GuestsTab
          group={group}
          onRosterChanged={refresh}
          onPreviewImport={(invitees) => groupsApi.previewInviteeImport(id!, invitees)}
          // The result is handed back, not discarded: it carries the rows the
          // write refused, and those are the rows nobody would otherwise hear
          // about.
          onImportInvitees={(invitees, rosterFingerprint) =>
            inviteeImportMutation.mutateAsync({ invitees, rosterFingerprint })
          }
          onSendInvites={(body) => sendInvitesMutation.mutateAsync(body)}
          onSegmentChange={(invitee, segment) =>
            inviteeImportMutation.mutate({ invitees: [inviteeToInput(invitee, segment)] })
          }
          rosterSaving={inviteeImportMutation.isLoading}
          sending={sendInvitesMutation.isLoading}
          importError={mutationErrorMessage(inviteeImportMutation.error)}
          sendError={mutationErrorMessage(sendInvitesMutation.error)}
          sendResult={sendInvitesMutation.data}
        />
      )}
      {tab === 'concierge' && <ConciergeTab group={group} onChanged={refresh} />}
      {tab === 'requests' && <ReservationsTab group={group} onChanged={refresh} />}
      {/* onOpenGuests: a roster-linked seat takes its name from the Guests tab,
          so the dinner sheet's "change it there" refusal has to be able to go
          there. Tab state lives here, so the hop does too. */}
      {tab === 'dinners' && (
        <DinnersTab
          group={group}
          onRosterChanged={refresh}
          onOpenGuests={() => setTab('guests')}
        />
      )}
      {tab === 'seating' && <SeatingTab group={group} />}
      {tab === 'broadcasts' && <BroadcastsTab group={group} onOpenOverview={() => setTab('overview')} />}
      {tab === 'export' && <HostExportTab group={group} />}
      <AskTripsbyPanel groupId={group.id} tab={tab} />
    </Box>
  );
}
